import { useEffect, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { Grid } from 'semantic-ui-react'
import * as yup from 'yup'

import {
  Alert,
  Button,
  FormikCheckbox,
  FormikDropdown,
  FormikLabelError,
  getFieldName,
  GridRowColumn,
  Link,
  Loader,
  Modal,
  Text,
} from '@/componentsV1/BaseComponents'
import { fetchAdminUserDocumentCategoriesIfNeeded } from '@/features/admin/user-document-categories/userDocumentCategories.slice'
import { fetchAdminUsersDocuments } from '@/actions/admin/userDocumentsActions'
import {
  AdminAnnualTaxFiling,
  adminSubmitTsk,
  getAdminSubmitTskFetchKey,
  getAdminUpdateAnnualTaxFilingFetchKey,
  updateAdminAnnualTaxFiling,
} from '../../taxes/adminAnnualTaxFilings.slice'
import { selectAdminTaxReturnDocumentsForTaxYear } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { useReselector } from '@/utils/sharedHooks'
import { TaxFormType } from '@/features/taxes/annual-taxes/questionnaires/constants'
import { needContractorFilingOption } from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import {
  convertUtcToLocalDate,
  DATE_FORMATS_LUXON,
  isoToUTCDateTime,
} from '@/utils/dateHelpers'
import { fetchTaxUserDocumentsIfNeededForUser } from '@/features/taxes/annual-taxes/taxUserDocuments.slice'
import { useAppDispatch } from '@/utils/typeHelpers'

const TaxSeasonKickoffModal = ({
  open,
  close,
  filing,
}: {
  open: boolean
  close: () => void
  filing: AdminAnnualTaxFiling
}) => {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(true)
  const isSubmittingUpdate = useReselector(
    getIsFetching,
    getAdminUpdateAnnualTaxFilingFetchKey(filing.id)
  )
  const isSubmittingTsk = useReselector(
    getIsFetching,
    getAdminSubmitTskFetchKey(filing.id)
  )
  const isSubmitting = isSubmittingUpdate || isSubmittingTsk
  const updateError = useReselector(
    getFetchError,
    getAdminUpdateAnnualTaxFilingFetchKey(filing.id)
  )
  const tskError = useReselector(
    getFetchError,
    getAdminSubmitTskFetchKey(filing.id)
  )
  const error = updateError || tskError
  const priorTaxYear = (Number(filing.year) - 1).toString()
  const [financialProfileId, setFinancialProfileId] = useState(-1)

  const formik = useFormik({
    initialValues: {
      annualTaxFormNeeds: filing.annualTaxFormNeeds,
      optedOut: Boolean(filing.optedOutAt),
      needContractorFiling: filing.needContractorFiling,
      isLateJoiner: Boolean(filing.isLateJoiner),
    },
    validateOnMount: true,
    onSubmit: async ({
      annualTaxFormNeeds,
      needContractorFiling,
      optedOut,
      isLateJoiner,
    }) => {
      const payload = {
        annualTaxFormNeeds,
        needContractorFiling,
        // Don't overwrite opted out date if it was set previously
        optedOutAt:
          !filing.optedOutAt && optedOut
            ? new Date().toISOString()
            : filing.optedOutAt && !optedOut
              ? null
              : undefined,
        // If opted out, late joiner should be cleared
        isLateJoiner:
          optedOut && !filing.optedOutAt
            ? null
            : Boolean(filing.isLateJoiner) !== isLateJoiner
              ? isLateJoiner
              : undefined,
      }

      // Opt-back-in users (TSK not yet submitted) → use dedicated submit-tsk endpoint
      // Already-completed-TSK users → use existing update endpoint
      const submitAction = !filing.pretaxSurveySubmittedAt
        ? adminSubmitTsk(filing.id, payload)
        : updateAdminAnnualTaxFiling(filing.id, payload)

      const res = await submitAction(dispatch)
      if (res) {
        close()
      }
    },
  })

  useEffect(() => {
    const fetch = async () => {
      const [_catRes, userDocRes, _taxUserDocRes] = await Promise.all([
        dispatch(fetchAdminUserDocumentCategoriesIfNeeded()),
        dispatch(fetchAdminUsersDocuments(filing.userId)),
        dispatch(
          fetchTaxUserDocumentsIfNeededForUser(filing.userId, filing.year)
        ),
      ])
      if (userDocRes?.financialProfileId) {
        setFinancialProfileId(userDocRes.financialProfileId)
      }

      setLoading(false)
    }

    if (open) {
      fetch()
    }
  }, [dispatch, filing, open])

  const taxReturnsForPriorYear = useReselector(
    selectAdminTaxReturnDocumentsForTaxYear,
    financialProfileId,
    priorTaxYear
  )
  const { values, submitForm, setFieldValue, isValid } = formik

  return (
    <FormikProvider value={formik}>
      <Modal onClose={close} open={open} closeIcon size="tiny">
        <Modal.Header>Tax Season Kickoff: {filing.userId}</Modal.Header>
        <Modal.Content>
          <Grid>
            {loading && (
              <GridRowColumn>
                <Loader />
              </GridRowColumn>
            )}
            {error && (
              <GridRowColumn>
                <Alert>{error.message}</Alert>
              </GridRowColumn>
            )}
            <GridRowColumn>
              <FormikDropdown
                label="Annual Tax Form Needs:"
                name={getFieldName<typeof values>('annualTaxFormNeeds')}
                optionValues={[TaxFormType.form1120s, TaxFormType.form1040]}
                multiple
                clearable
                fullWidth
                onChange={(val) => {
                  // If options are edited set opted out at as false
                  if (Array.isArray(val) && val.length) {
                    setFieldValue('optedOut', false)
                    if (filing.isLateJoinerTermsAcceptedAt) {
                      setFieldValue('isLateJoiner', true)
                    }
                  }
                }}
              />
              <Text>Currently Saved Values:</Text>
              <ul>
                {filing.annualTaxFormNeeds?.map((formNeed: TaxFormType) => (
                  <li key={formNeed}>
                    <Text>{formNeed}</Text>
                  </li>
                ))}
              </ul>
            </GridRowColumn>
            <GridRowColumn>
              <FormikLabelError
                name={getFieldName<typeof values>('optedOut')}
                schema={yup
                  .boolean()
                  .test(
                    'opted out',
                    "If there are no tax needs you must opt out.  If you don't mean to opt out this user, then set form needs",
                    (value) =>
                      value || Boolean(values.annualTaxFormNeeds?.length)
                  )}
              />
              <FormikCheckbox
                variant="default"
                name={getFieldName<typeof values>('optedOut')}
                label="Opted out"
                onChange={(val) => {
                  // Clear form needs if opted out
                  if (val) {
                    setFieldValue(
                      getFieldName<typeof values>('annualTaxFormNeeds'),
                      null
                    )
                    setFieldValue(
                      getFieldName<typeof values>('isLateJoiner'),
                      false
                    )
                  } else if (filing.isLateJoinerTermsAcceptedAt) {
                    setFieldValue(
                      getFieldName<typeof values>('isLateJoiner'),
                      true
                    )
                  }
                }}
              />
              <Text>
                Currently Saved Value:{' '}
                {filing.optedOutAt
                  ? isoToUTCDateTime(filing.optedOutAt).toFormat(
                      DATE_FORMATS_LUXON.DISPLAY_LONG
                    )
                  : 'Not opted out'}
              </Text>
            </GridRowColumn>
            <GridRowColumn>
              <FormikCheckbox
                variant="default"
                name={getFieldName<typeof values>('isLateJoiner')}
                label="Is Late Joiner"
                disabled={values.optedOut}
              />
              {filing.isLateJoinerTermsAcceptedAt && !values.optedOut && (
                <Text as="bodyXs">
                  Late joiner terms accepted on{' '}
                  {convertUtcToLocalDate(
                    filing.isLateJoinerTermsAcceptedAt
                  )?.toFormat(DATE_FORMATS_LUXON.DISPLAY_SIMPLE)}
                </Text>
              )}
            </GridRowColumn>
            <GridRowColumn>
              <FormikDropdown
                label="Needs contractor filing:"
                name={getFieldName<typeof values>('needContractorFiling')}
                optionValues={[
                  needContractorFilingOption.yes,
                  needContractorFilingOption.no,
                  needContractorFilingOption.unsure,
                ]}
                clearable
                fullWidth
              />
              <Text>Currently Saved Value: {filing.needContractorFiling}</Text>
            </GridRowColumn>
            <GridRowColumn>
              <Text as="h3">{priorTaxYear} Tax Returns</Text>
              {taxReturnsForPriorYear?.length === 0 ? (
                <Text>
                  There are no PYTR for this User. Please instruct them to
                  upload
                </Text>
              ) : (
                <>
                  <Text>
                    Here are the {priorTaxYear} returns that we have on file:
                  </Text>
                  {taxReturnsForPriorYear.map((document) => (
                    <Link
                      href={document.signedUrl || '#'}
                      newPage
                      key={`docRow-${document.id}`}
                    >
                      {document.fileDescription}
                    </Link>
                  ))}
                </>
              )}
            </GridRowColumn>
          </Grid>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={close}>Close</Button>
          <Button
            onClick={submitForm}
            variant="warning"
            loading={isSubmitting}
            disabled={!isValid}
          >
            Update TSK Responses
          </Button>
        </Modal.Actions>
      </Modal>
    </FormikProvider>
  )
}

export default TaxSeasonKickoffModal
